import { useCallback, useMemo } from 'react';

import type {
  CompareColumn,
  CompareEditControls,
} from '~/components/common/enrichment-compare-grid/compare-grid.types';

import type { EditLookup } from '../adapters/compare-grid-adapter';
import { selectEditedValue, useEnrichmentEditsStore } from '../store/enrichment-edits.store';

import { useUpdateEnrichedAttribute } from './use-update-enriched-attribute';

interface UseCompareEditsResult {
  /** Passed to the grid; its presence is what turns editing on. */
  controls: CompareEditControls;
  /** Passed to the row adapters so overrides replace the model output. */
  getEdit: EditLookup;
}

/**
 * Binds the persisted attribute-edit mutation and the local override mirror to
 * the compare grid's per-cell edit contract for one run. `scopeId` (an experiment
 * id or workflow id) keys every override so switching runs never shows another
 * run's values; `connectorCode` selects the enrichment table (`META` for the
 * scheduled flow, undefined → `GMC`).
 */
export function useCompareEdits(scopeId: string, connectorCode?: string): UseCompareEditsResult {
  // Subscribe to the map itself so any override re-renders the grid.
  const overrides = useEnrichmentEditsStore((s) => s.overrides);
  const setOverride = useEnrichmentEditsStore((s) => s.setOverride);
  const resetFieldInStore = useEnrichmentEditsStore((s) => s.resetField);
  const updateAttribute = useUpdateEnrichedAttribute(connectorCode);

  const getEdit = useCallback<EditLookup>(
    (productId, fieldKey) => selectEditedValue({ overrides }, scopeId, productId, fieldKey),
    [overrides, scopeId],
  );

  // Persists one cell, then mirrors the saved value locally. `value` already arrives
  // in the attribute's own shape (parsed for `json` fields). `mutateAsync` rejects on
  // failure, so the override is written only after a confirmed save and the grid's
  // viewer keeps the operator's draft open on error.
  const commitCell = useCallback(
    async (productId: string, column: CompareColumn, value: unknown) => {
      await updateAttribute.mutateAsync({
        productId,
        field: column.wireField,
        label: column.label,
        value,
      });
      setOverride(scopeId, productId, column.key, value);
    },
    [updateAttribute, setOverride, scopeId],
  );

  const resetCell = useCallback(
    (productId: string, fieldKey: string) => {
      resetFieldInStore(scopeId, productId, fieldKey);
    },
    [resetFieldInStore, scopeId],
  );

  const controls = useMemo<CompareEditControls>(
    () => ({ commitCell, resetCell }),
    [commitCell, resetCell],
  );

  return { controls, getEdit };
}
